/**
 * Transaction History Component
 * Lists and tracks all card transactions with authorization status
 * Trust Marketplace - Phase 3: Payment Infrastructure (Stripe Issuing)
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';
import {
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Eye,
  Filter,
  Download,
  ShoppingBag,
  MapPin,
  Calendar,
  DollarSign,
  RefreshCw
} from 'lucide-react';
import { useTabbedChrome } from '@/components/weavein';

interface TransactionHistoryProps {
  cardId?: number;
  memberId?: number;
}

interface CardTransaction {
  id: number;
  transaction_number: string;
  card_id: number;
  card_last4?: string;
  member_id: number;
  member_name?: string;
  stripe_transaction_id: string;
  transaction_type: string;
  amount: number;
  currency: string;
  merchant_name?: string;
  merchant_category: string;
  merchant_city?: string;
  merchant_state?: string;
  authorization_status: string;
  settlement_status: string;
  transaction_date: string;
  settled_date?: string;
  authorization_code?: string;
  decline_reason?: string;
  disputed: boolean;
  dispute_reason?: string;
  created_at: string;
}

const TRANSACTION_TYPES = [
  { value: 'PURCHASE', label: 'Purchase' },
  { value: 'REFUND', label: 'Refund' },
  { value: 'AUTHORIZATION', label: 'Authorization' },
  { value: 'REVERSAL', label: 'Reversal' }
];

const AUTHORIZATION_STATUSES = [
  { value: 'APPROVED', label: 'Approved' },
  { value: 'DECLINED', label: 'Declined' },
  { value: 'PENDING', label: 'Pending' }
];

const SETTLEMENT_STATUSES = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'SETTLED', label: 'Settled' },
  { value: 'REVERSED', label: 'Reversed' }
];

const TransactionHistory: React.FC<TransactionHistoryProps> = ({ cardId, memberId }) => {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const { fmt } = useFormattedDate();
  const [transactions, setTransactions] = useState<CardTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterAuthStatus, setFilterAuthStatus] = useState<string>('');
  const [filterSettlementStatus, setFilterSettlementStatus] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterDateFrom, setFilterDateFrom] = useState<string>('');
  const [filterDateTo, setFilterDateTo] = useState<string>('');

  // Modal states
  const [selectedTransaction, setSelectedTransaction] = useState<CardTransaction | null>(null);
  const [isDisputeModalOpen, setIsDisputeModalOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');

  useEffect(() => {
    fetchTransactions();
  }, [cardId, memberId, filterAuthStatus, filterSettlementStatus, filterType]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (cardId) params.append('card_id', cardId.toString());
      if (memberId) params.append('member_id', memberId.toString());
      if (filterAuthStatus) params.append('authorization_status', filterAuthStatus);
      if (filterSettlementStatus) params.append('settlement_status', filterSettlementStatus);
      if (filterType) params.append('transaction_type', filterType);
      if (filterDateFrom) params.append('date_from', filterDateFrom);
      if (filterDateTo) params.append('date_to', filterDateTo);

      const url = getWeaveinApiUrl(`/api/card-transactions?${params.toString()}`);

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch transactions: ${response.status}`);
      }

      const data = await response.json();
      setTransactions(data);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fileDispute = async () => {
    if (!selectedTransaction || !disputeReason.trim()) {
      alert('Please provide a dispute reason');
      return;
    }

    try {
      const url = getWeaveinApiUrl(`/api/card-transactions/${selectedTransaction.id}/dispute`);

      const response = await authFetch(url, {
        method: 'POST',
        jsonBody: {
          reason: disputeReason
        }
      });

      if (!response.ok) {
        throw new Error('Failed to file dispute');
      }

      setDisputeReason('');
      setIsDisputeModalOpen(false);
      setSelectedTransaction(null);
      fetchTransactions();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to file dispute');
    }
  };

  const getAuthStatusColor = (status: string): string => {
    switch (status.toUpperCase()) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'DECLINED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSettlementStatusColor = (status: string): string => {
    switch (status.toUpperCase()) {
      case 'SETTLED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'PENDING':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'REVERSED':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (authStatus: string) => {
    switch (authStatus.toUpperCase()) {
      case 'APPROVED':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'DECLINED':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'PENDING':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return fmt.dateTime(dateString);
  };

  // Calculate summary metrics
  const totalTransactions = transactions.length;
  const approvedCount = transactions.filter(tx => tx.authorization_status === 'APPROVED').length;
  const declinedCount = transactions.filter(tx => tx.authorization_status === 'DECLINED').length;
  const totalSpent = transactions
    .filter(tx => tx.authorization_status === 'APPROVED' && tx.transaction_type === 'PURCHASE')
    .reduce((sum, tx) => sum + tx.amount, 0);
  const disputedCount = transactions.filter(tx => tx.disputed).length;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 bg-red-50 border-red-200">
        <p className="text-red-800">Error: {error}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {!tabbed && <h2 className="text-2xl font-bold text-gray-900">{t('payments.transaction_history', 'Transaction History')}</h2>}
          <p className="text-sm text-gray-600 mt-1">{t('payments.track_all_card_transactions_and', 'Track all card transactions and authorizations')}</p>
        </div>
        <Button variant="outline" onClick={fetchTransactions}>
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('payments.refresh', 'Refresh')}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="w-4 h-4 text-blue-600" />
            <span className="text-xs text-blue-700 font-medium">{t('payments.total', 'Total')}</span>
          </div>
          <div className="text-2xl font-bold text-blue-900">{totalTransactions}</div>
          <div className="text-xs text-blue-600 mt-1">transactions</div>
        </Card>

        <Card className="p-4 bg-green-50 border-green-200">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-xs text-green-700 font-medium">{t('payments.approved', 'Approved')}</span>
          </div>
          <div className="text-2xl font-bold text-green-900">{approvedCount}</div>
          <div className="text-xs text-green-600 mt-1">
            {totalTransactions > 0 ? Math.round((approvedCount / totalTransactions) * 100) : 0}%
          </div>
        </Card>

        <Card className="p-4 bg-red-50 border-red-200">
          <div className="flex items-center gap-2 mb-1">
            <XCircle className="w-4 h-4 text-red-600" />
            <span className="text-xs text-red-700 font-medium">{t('payments.declined', 'Declined')}</span>
          </div>
          <div className="text-2xl font-bold text-red-900">{declinedCount}</div>
          <div className="text-xs text-red-600 mt-1">
            {totalTransactions > 0 ? Math.round((declinedCount / totalTransactions) * 100) : 0}%
          </div>
        </Card>

        <Card className="p-4 bg-purple-50 border-purple-200">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-purple-600" />
            <span className="text-xs text-purple-700 font-medium">{t('payments.total_spent', 'Total Spent')}</span>
          </div>
          <div className="text-2xl font-bold text-purple-900">{formatCurrency(totalSpent)}</div>
          <div className="text-xs text-purple-600 mt-1">approved</div>
        </Card>

        <Card className="p-4 bg-yellow-50 border-yellow-200">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-yellow-600" />
            <span className="text-xs text-yellow-700 font-medium">{t('payments.disputes', 'Disputes')}</span>
          </div>
          <div className="text-2xl font-bold text-yellow-900">{disputedCount}</div>
          <div className="text-xs text-yellow-600 mt-1">filed</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
          <div>
            <Label htmlFor="filter_auth_status">{t('payments.auth_status', 'Auth Status')}</Label>
            <Select value={filterAuthStatus} onValueChange={setFilterAuthStatus}>
              <SelectTrigger>
                <SelectValue placeholder={t('payments.all_1', 'All')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">{t('payments.all_statuses', 'All Statuses')}</SelectItem>
                {AUTHORIZATION_STATUSES.map(status => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="filter_settlement_status">{t('payments.settlement', 'Settlement')}</Label>
            <Select value={filterSettlementStatus} onValueChange={setFilterSettlementStatus}>
              <SelectTrigger>
                <SelectValue placeholder={t('payments.all_2', 'All')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">{t('payments.all', 'All')}</SelectItem>
                {SETTLEMENT_STATUSES.map(status => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="filter_type">{t('payments.type', 'Type')}</Label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger>
                <SelectValue placeholder={t('payments.all_3', 'All')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">{t('payments.all_types', 'All Types')}</SelectItem>
                {TRANSACTION_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="filter_date_from">{t('payments.from_date', 'From Date')}</Label>
            <Input
              id="filter_date_from"
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
            />
          </div>

          <div className="flex items-end gap-2">
            <Button variant="outline" onClick={fetchTransactions} className="flex-1">
              <Filter className="w-4 h-4 mr-2" />
              {t('payments.apply', 'Apply')}
            </Button>
            <Button variant="outline">
              <Download className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Transactions List */}
      {transactions.length === 0 ? (
        <Card className="p-12 text-center">
          <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('payments.no_transactions_yet', 'No transactions yet')}</h3>
          <p className="text-sm text-gray-600">
            {t('payments.transactions_will_appear_here_once', 'Transactions will appear here once cards are used')}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {transactions.map(transaction => (
            <Card key={transaction.id} className="p-5 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  {getStatusIcon(transaction.authorization_status)}
                </div>

                <div className="flex-1 min-w-0">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-lg font-semibold text-gray-900">
                          {formatCurrency(transaction.amount, transaction.currency)}
                        </h4>
                        <Badge className={getAuthStatusColor(transaction.authorization_status)}>
                          {transaction.authorization_status}
                        </Badge>
                        <Badge className={getSettlementStatusColor(transaction.settlement_status)}>
                          {transaction.settlement_status}
                        </Badge>
                        {transaction.disputed && (
                          <Badge className="bg-orange-100 text-orange-800 border-orange-200">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {t('payments.disputed', 'Disputed')}
                          </Badge>
                        )}
                      </div>

                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <span>{transaction.transaction_number}</span>
                        {transaction.merchant_name && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <ShoppingBag className="w-3 h-3" />
                              {transaction.merchant_name}
                            </span>
                          </>
                        )}
                        {transaction.card_last4 && (
                          <>
                            <span>•</span>
                            <span className="font-mono">•••• {transaction.card_last4}</span>
                          </>
                        )}
                      </div>
                    </div>

                    <Dialog>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedTransaction(transaction)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {t('payments.details', 'Details')}
                      </Button>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>{t('payments.transaction_details', 'Transaction Details')}</DialogTitle>
                        </DialogHeader>
                        {selectedTransaction && selectedTransaction.id === transaction.id && (
                          <div className="space-y-4 mt-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label className="text-xs text-gray-500">{t('payments.transaction_number', 'Transaction Number')}</Label>
                                <p className="font-mono text-sm">{selectedTransaction.transaction_number}</p>
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500">{t('payments.amount', 'Amount')}</Label>
                                <p className="text-lg font-bold text-green-600">
                                  {formatCurrency(selectedTransaction.amount, selectedTransaction.currency)}
                                </p>
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500">{t('payments.type_1', 'Type')}</Label>
                                <Badge variant="outline">{selectedTransaction.transaction_type}</Badge>
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500">{t('payments.authorization_status', 'Authorization Status')}</Label>
                                <Badge className={getAuthStatusColor(selectedTransaction.authorization_status)}>
                                  {selectedTransaction.authorization_status}
                                </Badge>
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500">{t('payments.settlement_status', 'Settlement Status')}</Label>
                                <Badge className={getSettlementStatusColor(selectedTransaction.settlement_status)}>
                                  {selectedTransaction.settlement_status}
                                </Badge>
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500">{t('payments.transaction_date', 'Transaction Date')}</Label>
                                <p className="text-sm">{formatDate(selectedTransaction.transaction_date)}</p>
                              </div>
                              {selectedTransaction.settled_date && (
                                <div>
                                  <Label className="text-xs text-gray-500">{t('payments.settled_date', 'Settled Date')}</Label>
                                  <p className="text-sm">{formatDate(selectedTransaction.settled_date)}</p>
                                </div>
                              )}
                              {selectedTransaction.merchant_name && (
                                <div className="col-span-2">
                                  <Label className="text-xs text-gray-500">{t('payments.merchant', 'Merchant')}</Label>
                                  <p className="font-medium">{selectedTransaction.merchant_name}</p>
                                  <p className="text-sm text-gray-600">
                                    {selectedTransaction.merchant_category}
                                    {selectedTransaction.merchant_city && ` • ${selectedTransaction.merchant_city}`}
                                    {selectedTransaction.merchant_state && `, ${selectedTransaction.merchant_state}`}
                                  </p>
                                </div>
                              )}
                              {selectedTransaction.authorization_code && (
                                <div>
                                  <Label className="text-xs text-gray-500">{t('payments.auth_code', 'Auth Code')}</Label>
                                  <p className="font-mono text-sm">{selectedTransaction.authorization_code}</p>
                                </div>
                              )}
                              {selectedTransaction.decline_reason && (
                                <div className="col-span-2">
                                  <Label className="text-xs text-gray-500">{t('payments.decline_reason', 'Decline Reason')}</Label>
                                  <p className="text-sm text-red-600">{selectedTransaction.decline_reason}</p>
                                </div>
                              )}
                              {selectedTransaction.disputed && selectedTransaction.dispute_reason && (
                                <div className="col-span-2">
                                  <Label className="text-xs text-gray-500">{t('payments.dispute_reason', 'Dispute Reason')}</Label>
                                  <p className="text-sm text-orange-600">{selectedTransaction.dispute_reason}</p>
                                </div>
                              )}
                            </div>

                            {!selectedTransaction.disputed && selectedTransaction.authorization_status === 'APPROVED' && (
                              <div className="pt-4 border-t">
                                <Button
                                  className="w-full bg-orange-600 hover:bg-orange-700"
                                  onClick={() => {
                                    setIsDisputeModalOpen(true);
                                  }}
                                >
                                  <AlertTriangle className="w-4 h-4 mr-2" />
                                  {t('payments.file_dispute', 'File Dispute')}
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>
                  </div>

                  {/* Merchant Info */}
                  {transaction.merchant_name && (
                    <div className="flex items-center gap-4 mb-3 text-sm">
                      <Badge variant="outline" className="text-xs">
                        {transaction.merchant_category}
                      </Badge>
                      {(transaction.merchant_city || transaction.merchant_state) && (
                        <span className="flex items-center gap-1 text-gray-600">
                          <MapPin className="w-3 h-3" />
                          {transaction.merchant_city}
                          {transaction.merchant_state && `, ${transaction.merchant_state}`}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Timeline */}
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(transaction.transaction_date)}
                    </span>
                    {transaction.settled_date && transaction.settlement_status === 'SETTLED' && (
                      <>
                        <span>→</span>
                        <span className="text-green-600 font-medium">
                          Settled: {formatDate(transaction.settled_date)}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Decline Reason */}
                  {transaction.decline_reason && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                      <span className="text-sm text-red-800">
                        Declined: {transaction.decline_reason}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Dispute Modal */}
      <Dialog open={isDisputeModalOpen} onOpenChange={setIsDisputeModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('payments.file_transaction_dispute', 'File Transaction Dispute')}</DialogTitle>
          </DialogHeader>
          {selectedTransaction && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">{t('payments.amount_1', 'Amount:')}</span>
                  <span className="text-lg font-bold">
                    {formatCurrency(selectedTransaction.amount, selectedTransaction.currency)}
                  </span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">{t('payments.merchant_1', 'Merchant:')}</span>
                  <span className="font-medium">{selectedTransaction.merchant_name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{t('payments.date', 'Date:')}</span>
                  <span className="text-sm">{formatDate(selectedTransaction.transaction_date)}</span>
                </div>
              </div>

              <div>
                <Label htmlFor="dispute_reason">{t('payments.dispute_reason_1', 'Dispute Reason *')}</Label>
                <Textarea
                  id="dispute_reason"
                  value={disputeReason}
                  onChange={(e) => setDisputeReason(e.target.value)}
                  placeholder={t('payments.describe_the_reason_for_this', 'Describe the reason for this dispute...')}
                  rows={4}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsDisputeModalOpen(false)}>
                  {t('payments.cancel', 'Cancel')}
                </Button>
                <Button
                  onClick={fileDispute}
                  disabled={!disputeReason.trim()}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  {t('payments.file_dispute_1', 'File Dispute')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransactionHistory;
